import React, { FC, useEffect, useState } from 'react';
import {
  Paper,
  Grid,
  Typography,
  IconButton,
  Box,
  Link,
  Dialog,
  DialogTitle,
  Card,
  CircularProgress,
  DialogContent,
} from '@material-ui/core';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import ROUTES from '../../../../routes';
import OutageFilterList from './OutageFilterList';
import {
  OutageDetailsByCounty,
  ZipCodeInfo,
} from '../../../../__generated__/pge-types';
import Pagination from '../../../pagination';
import useGTMUtil from '../../../../lib/useGTMUtil';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    zipModal: {
      color: colors.noirBlur,
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '922px',
      },
    },
    outageByZipModal: {
      backgroundColor: colors.modalBlue,
      padding: theme.spacing(3),
      boxShadow: theme.shadows[5],
      borderRadius: '5px',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4, 3, 3, 3),
      },
    },
    outageByZipNote: {
      margin: theme.spacing(0, 0, 3, 0),
      fontSize: '1.25rem',
    },
    closeBtn: {
      position: 'absolute',
      top: 24,
      right: 24,
      padding: 0,
    },
    filterWrap: {
      backgroundColor: colors.white,
    },
    outageFooterNote: {
      fontSize: '1rem',
    },
    noOutagesError: {
      margin: '10px',
    },
    outagePopupTitle: {
      backgroundColor: colors.modalBlue,
      paddingBottom: '0px',
    },
    outagePopupContent: {
      padding: '0px',
    },
    outageContentText: {
      paddingTop: '0px',
    },
  }),
);

interface Props {
  handleOutageByZipClick: (show: boolean) => void;
  handleZipcodeClick: (
    countyName: string | null | undefined,
    zipCode?: string | null | undefined,
    zipIndex?: number | null | undefined,
    zipCodes?: Array<ZipCodeInfo> | undefined | null,
  ) => void;
  showOutageByZipPopup: boolean;
  outageByCounty?: OutageDetailsByCounty[];
  isOutageByCountyLoading: boolean;
  isOutageByCountyError: boolean;
}

const OutageByZipCodePopup: FC<Props> = (props: Props) => {
  const {
    handleOutageByZipClick,
    showOutageByZipPopup,
    handleZipcodeClick,
    outageByCounty,
    isOutageByCountyLoading,
    isOutageByCountyError,
  } = props;
  const [showEstimate, setShowEstimate] = useState(false);
  const [outageListPagination, setOutageListPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 5,
    pageSizes: [5],
  });
  const [outageListCounty, setOutageListCounty] = useState<
    OutageDetailsByCounty[]
  >([]);
  const { t } = useTranslation();
  const classes = useStyles();
  const { postGTMModalVisibilityData } = useGTMUtil();

  const handlePageChange = (page: number) => {
    setOutageListPagination({
      ...outageListPagination,
      page,
    });
    if (outageByCounty) {
      setOutageListCounty(
        outageByCounty.slice(
          (page - 1) * outageListPagination.pageSize,
          (page - 1) * outageListPagination.pageSize +
            outageListPagination.pageSize,
        ),
      );
    }
  };

  useEffect(() => {
    if (outageByCounty) {
      const { page, pageSize } = outageListPagination;
      setOutageListPagination({
        ...outageListPagination,
        count: outageByCounty.length,
        page: 1,
      });
      setOutageListCounty(
        outageByCounty.slice(
          (page - 1) * pageSize,
          (page - 1) * pageSize + pageSize,
        ),
      );
    }
  }, [outageByCounty]);

  //Push modal visibility status to the GTM
  useEffect(() => {
    const GTMPageData = {
      page: 'Outages By ZipCode',
      siteSection: 'Outages By ZipCode Modal',
    };

    postGTMModalVisibilityData({ ...GTMPageData, isVisible: true });

    return () =>
      postGTMModalVisibilityData({ ...GTMPageData, isVisible: false });
  }, []);

  return (
    <Dialog
      open={showOutageByZipPopup}
      onClose={() => {
        handleOutageByZipClick(false);
      }}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      className={classes.zipModal}
      disableScrollLock
    >
      <DialogTitle
        id="scroll-dialog-title"
        className={classes.outagePopupTitle}
      >
        <Grid className={'outageTitle'} container justify="space-between">
          <Typography
            variant={'h1'}
            noWrap
            gutterBottom
            data-testid="outageListHeader"
          >
            {t('OUTAGE_LIST')}
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={() => {
              handleOutageByZipClick(false);
            }}
            className={classes.closeBtn}
          >
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.outagePopupContent}>
        <Paper
          className={clsx(classes.outageByZipModal, classes.outageContentText)}
        >
          <Box>
            <Typography className={classes.outageByZipNote} variant={'body2'}>
              {t('OUTAGE_LIST_ZIP_NOTE')}
            </Typography>
            <Card className={classes.filterWrap}>
              {Array.isArray(outageByCounty) && outageByCounty.length ? (
                <OutageFilterList
                  handleZipcodeClick={handleZipcodeClick}
                  outageList={outageListCounty}
                />
              ) : isOutageByCountyLoading ? (
                <Box
                  display="flex"
                  margin={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  <CircularProgress />
                </Box>
              ) : isOutageByCountyError ? (
                <Typography className={classes.noOutagesError}>
                  {t('NO_REPORTED_OUTAGES')}
                </Typography>
              ) : (
                <Typography className={classes.noOutagesError}>
                  {t('NO_REPORTED_OUTAGES')}
                </Typography>
              )}
            </Card>
          </Box>
          <Box>
            <Pagination
              onPageChange={handlePageChange}
              {...outageListPagination}
            />
          </Box>
          <Box className={classes.outageFooterNote} mt={1} mb={2}>
            <Typography data-testid="outage-info" variant={'body2'}>
              {t('OUTAGE_INFO_IS_APPROXIMATE')}{' '}
              {
                <Link
                  href="#"
                  data-testid="show-estimate-modal"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    setShowEstimate(true);
                  }}
                >
                  {t('ESTIMATED_TIMES')}
                </Link>
              }{' '}
              {t('ESTIMATES_ONLY')}
            </Typography>
            <Dialog
              open={showEstimate}
              onClose={() => setShowEstimate(false)}
              scroll={'body'}
              aria-labelledby="scroll-dialog-title"
              aria-describedby="scroll-dialog-description"
              disableScrollLock
            >
              <div className={classes.outageByZipModal}>
                <div className={'closeButton'}>
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    onClick={() => setShowEstimate(false)}
                  >
                    <Close />
                  </IconButton>
                </div>
                <Typography
                  data-testid="about-estimates"
                  variant={'h2'}
                  gutterBottom
                >
                  {t('ABOUT_ESTIMATES')}
                </Typography>
                <Typography
                  data-testid="about-estimates-2"
                  variant={'h4'}
                  style={{ marginTop: '.5rem', marginRight: '.75rem' }}
                >
                  {t('ABOUT_ESTIMATES_2')}
                </Typography>
                <Typography
                  data-testid="about-estimates-3"
                  variant={'h4'}
                  style={{ marginTop: '.5rem', marginRight: '.75rem' }}
                >
                  {t('ABOUT_ESTIMATES_3')}
                </Typography>
              </div>
            </Dialog>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default OutageByZipCodePopup;
